import type { TimeUnit, ZoomSliderMode } from 'commons';
import type { DtisZoomEvent } from '../../../../components/dtis-multilane-chart/zoomEvent';

/**
 * Action to generate the EChart configuration for a specific view.
 */
export class GenerateEChartConfig {
  static readonly type = '[MultiView] Generate EChart Config';
  /**
   * @param viewId - The ID of the view for which the configuration is generated.
   */
  constructor(public viewId?: string) {}
}

/**
 * Action to set the zoom level for a multi-view chart.
 */
export class SetZoom {
  static readonly type = '[MultiView] Set Zoom';
  /**
   * @param zoom - The zoom event containing zoom details.
   */
  constructor(public readonly zoom: DtisZoomEvent) {}
}

/**
 * Action to set the mode of the zoom slider.
 */
export class SetZoomSliderMode {
  static readonly type = '[LaneToolboxComponent] setZoomSliderMode';
  /**
   * @param zoomSliderMode - The mode of the zoom slider.
   */
  constructor(public zoomSliderMode: ZoomSliderMode) {}
}

/**
 * Action to set the loading state for specific channels.
 */
export class SetLoading {
  static readonly type = '[MultiView] Set Loading';
  /**
   * @param loadingChannels - The list of channels currently loading.
   */
  constructor(public loadingChannels: string[]) {}
}
/**
 * Action to reset the loading state.
 */
export class ResetLoading {
  static readonly type = '[MultiView] Reset Loading';
}

/**
 * Action to export a file with specified parameters.
 */
export class ExportFile {
  static readonly type = '[MultiView] export]';
  /**
   * @param payload - The export parameters including filename, resolution, type, and optional lane filters.
   */
  constructor(
    public readonly payload: {
      filename: string;
      resolution: { windowUnit: TimeUnit; windowSize: number };
      type: 'csv' | 'json';
      laneIds?: string[];
    }
  ) {}
}

/**
 * Action to export table data with lane-specific filtering.
 */
export class ExportTableData {
  static readonly type = '[TableView] Export Table Data';
  /**
   * @param data - The table data to export.
   * @param filename - The filename for the export.
   * @param type - The export format.
   * @param laneFilter - The lane filter applied ('all' or specific lane ID).
   */
  constructor(
    public readonly data: unknown[],
    public readonly filename: string,
    public readonly type: 'csv' | 'json',
    public readonly laneFilter: string
  ) {}
}

/**
 * Action to export data as a CSV file.
 */
export class ExportAsCSV {
  static readonly type = '[MultiView] exportAsCSV]';
}

/**
 * Action to export data as a JSON file.
 */
export class ExportAsJSON {
  static readonly type = '[MultiView] exportAsJSON]';
}

/**
 * Action indicating the success of a file export operation.
 */
export class ExportFileSUCCESS {
  static readonly type = '[MultiView] export success]';
}

/**
 * Action indicating the failure of a file export operation.
 */
export class ExportFileFAILURE {
  static readonly type = '[MultiView] export failure]';
  /**
   * @param filename - The name of the file that failed to export.
   * @param type - The type of the file (e.g., 'csv' or 'json').
   * @param resolution - The resolution details of the export.
   */
  constructor(
    public readonly filename: string,
    public readonly type: string,
    public readonly resolution: unknown
  ) {}
}
